import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SiteBreadcrumb from '../../components/layout/SiteBreadcrumb';
import BestFacilities from '../../components/shared/BestFacilities';
import { useSelector, useDispatch } from 'react-redux';
import { LOADER_DURATION } from '../../../helpers/Constants';
import { showErrorMsg, showSuccessMsg, validateAnyFormField } from '../../../helpers/ValidationHelper';
import { MakeApiCallSynchronous, MakeApiCallAsync } from '../../../helpers/ApiHelpers';
import Config from '../../../helpers/Config';
import rootAction from '../../../stateManagment/actions/rootAction';
import { Helmet } from 'react-helmet';
import { getLanguageCodeFromSession, GetLocalizationControlsJsonDataForScreen, replaceLoclizationLabel } from '../../../helpers/CommonHelper';
import GlobalEnums from '../../../helpers/GlobalEnums';
import './BecomeVendor.css';
import './UpdateProfile.css';
import { getProfileSaveResult, makeProfileSaveParameters } from '../../../helpers/ProfileSave';
import { normalizeAddressName, parseProfileAddress, buildProfileAddress, parsePinOffices } from '../../../helpers/ProfileAddress';

const UpdateProfile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [siteTitle, setSiteTitle] = useState(Config['SITE_TTILE']);
    const [UserID, setUserID] = useState('');
    const [FirstName, setFirstName] = useState('');
    const [LastName, setLastName] = useState('');
    const [MobileNo, setMobileNo] = useState('');
    const [AddressLineOne, setAddressLineOne] = useState('');
    const [Village, setVillage] = useState('');
    const [Taluk, setTaluk] = useState('');
    const [postalDistrict, setPostalDistrict] = useState('');
    const [postOffices, setPostOffices] = useState([]);
    const [pinLoading, setPinLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [addressEdited, setAddressEdited] = useState(false);
    const savingRef = useRef(false);
    const [pinError, setPinError] = useState('');
    const [statesLoading, setStatesLoading] = useState(true);
    const [citiesLoading, setCitiesLoading] = useState(false);
    const pinController = useRef(null);
    const [PostalCode, setPostalCode] = useState('');
    const [StateProvinceId, setStateProvinceId] = useState('');
    const [CityId, setCityId] = useState('');
    const [StatesProvincesList, setStatesProvincesList] = useState([]);
    const [CitiesList, setCitiesList] = useState([]);
    const [LocalizationLabelsArray, setLocalizationLabelsArray] = useState([]);

    const loginUserDataJson = useSelector(state => state.userReducer.user);
    const loginUser = JSON.parse(loginUserDataJson ?? "{}");

    if (!loginUser?.UserID || Number(loginUser.UserID) < 1) {
        navigate('/' + getLanguageCodeFromSession() + '/');
    }


    const HandleStateProvinceChagne = value => {
        setAddressEdited(true);
        pinController.current?.abort();
        setPinLoading(false);
        setPinError('');
        setStateProvinceId(value);
        setCityId('');
        setCitiesList([]);
        setPostOffices([]);
        setVillage(''); setTaluk(''); setPostalDistrict('');
        setPostalCode('');
    };

    useEffect(() => {
        let mounted = true;
        setCitiesList([]);
        if (!StateProvinceId) { setCitiesLoading(false); return; }
        setCitiesLoading(true);
        const load = async () => {
            const response = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_CITIES_LIST, null,
                {requestParameters: {StateProvinceId: Number(StateProvinceId), PageNo: 1, PageSize: 10000, recordValueJson: '[]'}},
                {Accept: 'application/json', 'Content-Type': 'application/json'}, 'POST', true);
            if (!response?.data?.data) throw new Error('Unable to load cities for this state.');
            const data = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            const cities = data.filter(city => Number(city.StateProvinceID ?? city.StateProvinceId) === Number(StateProvinceId));
            if (mounted) {
                setCitiesList(cities);
                const savedCityId = loginUser.CityID ?? loginUser.CityId;
                if (cities.some(city => String(city.CityID) === String(savedCityId))) setCityId(String(savedCityId));
            }
        };
        load().catch(error => { if (mounted) showErrorMsg(error.message); }).finally(() => { if (mounted) setCitiesLoading(false); });
        return () => { mounted = false; };
    }, [StateProvinceId]);

    useEffect(() => {
        if (!/^\d{6}$/.test(PostalCode || '') || !StatesProvincesList.length) return;
        const controller = new AbortController();
        pinController.current = controller;
        const timer = setTimeout(async () => {
            setPinLoading(true); setPinError('');
            try {
                const response = await fetch('https://api.postalpincode.in/pincode/' + PostalCode, {signal: controller.signal});
                if (!response.ok) throw new Error('PIN code lookup is unavailable. Please try again.');
                const offices = parsePinOffices(await response.json());
                if (!offices.length) throw new Error('No address found for this PIN code. Check the six digits.');
                if (controller.signal.aborted) return;
                const state = StatesProvincesList.find(item => normalizeAddressName(item.StateName) === normalizeAddressName(offices[0].State));
                if (!state) throw new Error('The PIN state is missing from the address list. Please select your state manually.');
                setStateProvinceId(String(state.StateProvinceID));
                setPostOffices(offices);
                setPostalDistrict(offices[0].District || '');
                const savedVillage = parseProfileAddress(loginUser.AddressLineOne).village;
                const chosen = offices.find(office => office.Name === savedVillage) || (offices.length === 1 ? offices[0] : null);
                setVillage(chosen?.Name || '');
                setTaluk(chosen?.Block && chosen.Block !== 'NA' ? chosen.Block : offices[0].Block === 'NA' ? '' : offices[0].Block || '');
            } catch (error) {
                if (!controller.signal.aborted) { setPostOffices([]); setPinError(error.message || 'Unable to look up PIN code.'); }
            } finally { if (!controller.signal.aborted) setPinLoading(false); }
        }, 350);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [PostalCode, StatesProvincesList]);

    useEffect(() => {
        if (!postOffices.length || !CitiesList.length) return;
        const names = postOffices.flatMap(office => [office.District, office.Block, office.Name]).map(normalizeAddressName);
        const match = CitiesList.find(city => names.includes(normalizeAddressName(city.CityName)));
        setCityId(current => match ? String(match.CityID) : CitiesList.some(city => String(city.CityID) === String(current)) ? current : '');
    }, [postOffices, CitiesList]);

    const changePostalCode = value => {
        setAddressEdited(true);
        pinController.current?.abort();
        setPinLoading(false); setPinError('');
        setPostOffices([]); setVillage(''); setTaluk(''); setPostalDistrict('');
        setPostalCode(value.replace(/\D/g, '').slice(0, 6));
    };

    const handleUpdateProfileForm = async (event) => {
        event.preventDefault();
        if (savingRef.current) return false;

        try {
            if (pinLoading || citiesLoading || statesLoading) { showErrorMsg('Please wait for the address lookup to finish.'); return false; }
            if (!/^\d{6}$/.test(PostalCode || '')) { showErrorMsg('Enter a valid six-digit PIN code.'); return false; }
            if (!(Number(StateProvinceId) > 0)) { showErrorMsg('Please select your state.'); return false; }
            if (addressEdited && postOffices.length && !Village) { showErrorMsg('Please select your village / locality.'); return false; }
            const savedAddress = buildProfileAddress(AddressLineOne, Village, Taluk, postalDistrict);
            if (savedAddress.length > 600) { showErrorMsg('Please keep your full shipping address within 600 characters.'); return false; }

            let isValid = false;
            let validationArray = [];


            isValid = validateAnyFormField('First Name', FirstName, 'text', null, 200, true);
            if (isValid == false) {
                validationArray.push({
                    isValid: isValid
                });
            }


            isValid = !String(LastName || '').trim() || validateAnyFormField('Last Name', LastName, 'text', null, 150, false);
            if (isValid == false) {
                validationArray.push({
                    isValid: isValid
                });
            }





            isValid = validateAnyFormField('Shipping address', AddressLineOne, 'text', null, 600, true);
            if (isValid == false) {
                validationArray.push({
                    isValid: isValid
                });
            }


            //--check if any field is not valid
            if (validationArray != null && validationArray.length > 0) {

                isValid = false;
                return false;
            } else {
                isValid = true;
            }

            if (isValid) {
                if (!(Number(UserID) > 0)) throw new Error('Please sign in again before updating your profile.');
                savingRef.current = true;
                setIsSaving(true);


                const headers = {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                }


                const param = {
                    requestParameters: makeProfileSaveParameters({UserID, FirstName, LastName, MobileNo,
                        AddressLineOne: savedAddress, CityId, StateProvinceId, PostalCode})
                };

                //--make api call for data operation
                const response = await MakeApiCallAsync(Config.END_POINT_NAMES['UPDATE_PROFILE'], null, param, headers, "POST", true);
                const result = getProfileSaveResult(response);
                if (!result.success) throw new Error(result.message);
                showSuccessMsg("Profile Updated Successfully!");

                loginUser.FirstName = FirstName;
                loginUser.LastName = LastName;
                loginUser.MobileNo = MobileNo;
                loginUser.AddressLineOne = savedAddress;
                loginUser.CityID = CityId; loginUser.CityId = CityId;
                loginUser.CityName = CitiesList.find(city => String(city.CityID) === String(CityId))?.CityName || postalDistrict;
                loginUser.StateName = StatesProvincesList.find(state => String(state.StateProvinceID) === String(StateProvinceId))?.StateName || '';
                loginUser.Village = Village; loginUser.Taluk = Taluk;
                loginUser.StateProvinceID = StateProvinceId; loginUser.StateProvinceId = StateProvinceId;
                loginUser.PostalCode = PostalCode;

                //--save user data in session
                localStorage.setItem("user", JSON.stringify(loginUser));
                dispatch(rootAction.userAction.setUser(JSON.stringify(loginUser)));

                setTimeout(() => {
                    navigate('/' + getLanguageCodeFromSession() + '/');
                }, 1000);


            }



        } catch (err) {
            console.log(err);
            showErrorMsg(err.message || 'Unable to save your profile. Please try again.');

            return false;

        } finally {
            savingRef.current = false;
            setIsSaving(false);
            //--stop loader
            setTimeout(() => {
                dispatch(rootAction.commonAction.setLoading(false));
            }, LOADER_DURATION);

        }
    }



    useEffect(() => {
        let mounted = true;
        const address = parseProfileAddress(loginUser.AddressLineOne);
        setUserID(loginUser.UserID || ''); setFirstName(loginUser.FirstName || '');
        setLastName(loginUser.LastName || ''); setMobileNo(loginUser.MobileNo || '');
        setAddressLineOne(address.street); setVillage(address.village); setTaluk(address.taluk); setPostalDistrict(address.district);
        setStateProvinceId(String(loginUser.StateProvinceID ?? loginUser.StateProvinceId ?? ''));
        setCityId(String(loginUser.CityID ?? loginUser.CityId ?? ''));
        setPostalCode(String(loginUser.PostalCode || ''));
        const load = async () => {
            const headers = {Accept: 'application/json', 'Content-Type': 'application/json'};
            let countryId = loginUser.CountryID ?? loginUser.CountryId;
            if (!countryId) {
                const countriesResponse = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_COUNTRIES_LIST, null,
                    {requestParameters: {PageNo: 1, PageSize: 1000, recordValueJson: '[]'}}, {...headers}, 'POST', true);
                const countries = typeof countriesResponse?.data?.data === 'string' ? JSON.parse(countriesResponse.data.data) : countriesResponse?.data?.data || [];
                countryId = countries.find(country => normalizeAddressName(country.CountryName) === 'india')?.CountryID;
            }
            const response = await MakeApiCallAsync(Config.END_POINT_NAMES.GET_STATES_PROVINCES_LIST, null,
                {requestParameters: {CountryId: countryId, PageNo: 1, PageSize: 1000, recordValueJson: '[]'}}, {...headers}, 'POST', true);
            if (!response?.data?.data) throw new Error('Unable to load states. Please reload and try again.');
            const states = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
            if (mounted) setStatesProvincesList(states);
            const labels = await GetLocalizationControlsJsonDataForScreen(GlobalEnums.Entities.UpdateProfile, null);
            if (mounted && labels?.length) setLocalizationLabelsArray(labels);
        };
        load().catch(error => { if (mounted) showErrorMsg(error.message); }).finally(() => { if (mounted) setStatesLoading(false); });
        return () => { mounted = false; pinController.current?.abort(); };
    }, []);

    return (
        <>
            <Helmet>
                <title>{siteTitle} - Update Profile</title>
                <meta name="description" content={siteTitle + " - Update Profile"} />
                <meta name="keywords" content="Update Profile"></meta>
            </Helmet>
            <SiteBreadcrumb title="Update Profile" />

            <section className="signup-area ptb-60 become-seller-page update-profile-page">
                <div className="container">
                    <div className="signup-content become-seller-content">
                        <div className="section-title">
                            <h2><span className="dot"></span>
                                <span id="lbl_uprofile_title">
                                    {LocalizationLabelsArray.length > 0 ?
                                        replaceLoclizationLabel(LocalizationLabelsArray, "Update Profile", "lbl_uprofile_title")
                                        :
                                        "Update Profile"
                                    }
                                </span>
                            </h2>
                        </div>

                        <form className="signup-form become-seller-form" onSubmit={handleUpdateProfileForm}>

                            <div className='row'>
                                <div className='col-lg-6 col-md-6'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "First Name", "lbl_uprofile_fname")
                                                :
                                                "First Name"
                                            }
                                            <span className="required-field">*</span></label>
                                        <input type="text" className="form-control" placeholder="Enter first name" id="FirstName" name="FirstName"
                                            required={true}
                                            value={FirstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className='col-lg-6 col-md-6'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "Last Name", "lbl_uprofile_lname")
                                                :
                                                "Last Name"
                                            }
                                            <span className="required-field">*</span></label>
                                        <input type="text" className="form-control" placeholder="Enter last name" id="LastName" name="LastName"
                                            required={true}
                                            value={LastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                        />
                                    </div>
                                </div>


                                <div className='col-lg-6 col-md-6'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "State/Province", "lbl_uprofile_state")
                                                :
                                                "State/Province"
                                            }
                                        </label>
                                        <div className="select-box">
                                            <select
                                                className="form-control"
                                                name="StateProvinceId"
                                                disabled={statesLoading || pinLoading}
                                                required={true}
                                                value={StateProvinceId}
                                                onChange={(e) => HandleStateProvinceChagne(e.target.value)}
                                            >
                                                <option value="">{statesLoading ? "Loading states..." : "Select State"}</option>
                                                {
                                                    StatesProvincesList?.map((item, idx) =>

                                                        <option key={item.StateProvinceID} value={item.StateProvinceID}>{item.StateName}</option>

                                                    )}


                                            </select>
                                        </div>
                                    </div>
                                </div>


                                <div className='col-lg-6 col-md-6'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "City", "lbl_uprofile_city")
                                                :
                                                "City"
                                            }
                                        </label>
                                        <div className="select-box">
                                            <select
                                                className="form-control"
                                                name="CityId"
                                                disabled={!StateProvinceId || citiesLoading || pinLoading}
                                                required={false}
                                                value={CityId}
                                                onChange={(e) => setCityId(e.target.value)}
                                            >
                                                <option value="">{citiesLoading ? "Loading cities..." : postalDistrict ? postalDistrict + " (PIN district)" : "Select City"}</option>
                                                {
                                                    CitiesList?.map((item, idx) =>

                                                        <option key={item.CityID} value={item.CityID}>{item.CityName}</option>

                                                    )}


                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className='col-lg-6 col-md-6'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "Contact No", "lbl_uprofile_contno")
                                                :
                                                "Contact No"
                                            }
                                        </label>
                                        <input type="text" className="form-control" placeholder="Enter contact no" id="MobileNo" name="MobileNo"
                                            value={MobileNo}
                                            onChange={(e) => setMobileNo(e.target.value)}
                                        />
                                    </div>
                                </div>


                                <div className='col-lg-6 col-md-6'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "Postal Code", "lbl_uprofile_pcode")
                                                :
                                                "Postal Code"
                                            }
                                        </label>
                                        <input type="text" inputMode="numeric" maxLength={6} pattern="[0-9]{6}" required className="form-control" placeholder="Enter 6-digit PIN code" id="PostalCode" name="PostalCode"
                                            value={PostalCode}
                                            onChange={(e) => changePostalCode(e.target.value)}
                                        />
                                        <small role="status" aria-live="polite">{pinLoading ? 'Looking up PIN code...' : pinError || (postOffices.length ? 'Address found. Select your village / locality below.' : 'Enter your PIN code to find your address.')}</small>
                                    </div>
                                </div>


                                <div className="col-lg-6 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="Village">Village / Locality</label>
                                        <select id="Village" name="Village" className="form-control" value={Village} disabled={!postOffices.length || pinLoading} required={postOffices.length > 0}
                                            onChange={event => {
                                                setVillage(event.target.value);
                                                const office = postOffices.find(item => item.Name === event.target.value);
                                                setTaluk(office?.Block === 'NA' ? '' : office?.Block || '');
                                                setPostalDistrict(office?.District || '');
                                            }}>
                                            <option value="">Select village / locality</option>
                                            {Village && !postOffices.some(office => office.Name === Village) && <option value={Village}>{Village}</option>}
                                            {[...new Set(postOffices.map(office => office.Name))].map(name => <option key={name} value={name}>{name}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-6 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="Taluk">Taluk / Block</label>
                                        <input id="Taluk" name="Taluk" type="text" className="form-control" value={Taluk} onChange={event => setTaluk(event.target.value)} placeholder="Filled from PIN code when available" />
                                    </div>
                                </div>
                                <div className='col-lg-12 col-md-12'>
                                    <div className="form-group">
                                        <label>
                                            {LocalizationLabelsArray.length > 0 ?
                                                replaceLoclizationLabel(LocalizationLabelsArray, "Shipping Address", "lbl_uprofile_shipaddress")
                                                :
                                                "Shipping Address"
                                            }
                                            <span className="required-field">*</span></label>
                                        <input type="text" className="form-control" placeholder="Enter shipping address" id="AddressLineOne" name="AddressLineOne"
                                            required={true}
                                            value={AddressLineOne}
                                            onChange={(e) => { setAddressLineOne(e.target.value); setAddressEdited(true); }}
                                        />
                                    </div>
                                </div>

                            </div>

                            <div className="seller-form-actions">
                                <button type="submit" disabled={isSaving || pinLoading || citiesLoading || statesLoading} className="btn btn-primary" id="lbl_uprofile_submit">
                                    {LocalizationLabelsArray.length > 0 ?
                                        replaceLoclizationLabel(LocalizationLabelsArray, "Update Profile", "lbl_uprofile_submit")
                                        :
                                        "Update Profile"
                                    }
                                </button>

                                <Link to={`/${getLanguageCodeFromSession()}/`} className="return-store" id="lbl_uprofile_gohome">
                                    {LocalizationLabelsArray.length > 0 ?
                                        replaceLoclizationLabel(LocalizationLabelsArray, "Go To Home Page", "lbl_uprofile_gohome")
                                        :
                                        "Go To Home Page"
                                    }
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            <BestFacilities />


        </>
    );
}

export default UpdateProfile;
